import type { Lang, LocalizedText } from "./types";

/** Round away floating-point noise (0.1 + 0.2 → 0.3) and drop trailing zeros. */
export function clean(n: number): number {
  const r = Number(n.toFixed(9));
  return Object.is(r, -0) ? 0 : r;
}

export function numStr(n: number, lang: Lang): string {
  const s = clean(n).toString();
  return lang === "pl" ? s.replace(".", ",") : s;
}

/** Number as localized text: 2.5 → { pl: "2,5", en: "2.5" }. */
export function num(n: number): LocalizedText {
  return { pl: numStr(n, "pl"), en: numStr(n, "en") };
}

/** Number with a unit: 12 km → { pl: "12 km", en: "12 km" }. */
export function numUnit(n: number, unitPl: string, unitEn: string = unitPl): LocalizedText {
  return { pl: `${numStr(n, "pl")} ${unitPl}`, en: `${numStr(n, "en")} ${unitEn}` };
}

export function frac(numerator: number, denominator: number): LocalizedText {
  const s = `${numerator}/${denominator}`;
  return { pl: s, en: s };
}

export function text(pl: string, en: string): LocalizedText {
  return { pl, en };
}

/** Wrap a negative number in brackets for display inside an expression: −3 → (−3). */
export function signed(n: number): string {
  return n < 0 ? `(${n})` : `${n}`;
}

export function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

export function lcm(a: number, b: number): number {
  return Math.abs(a * b) / gcd(a, b);
}

export function simplify(numerator: number, denominator: number): { num: number; den: number } {
  const g = gcd(numerator, denominator) || 1;
  let n = numerator / g;
  let d = denominator / g;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  return { num: n, den: d };
}
